/**
 * Surcouche thème de l'app Biscuits Bundles (« compose ton pack »).
 *
 * Portage refonte du dev prod `_dev/js/components/biscuits-bundle.js` (798 l.) :
 *  1. Auto-sélection de la 1re variante dispo quand la présélectionnée est OOS
 *     (tourne AVANT l'init du script defer de l'app, patch les JSON de config).
 *  2. Dropdown de saveurs custom coloré (couleurs depuis `#biscuits-bundle-options`,
 *     généré du métaobjet `swatch` — plus les `pr_option_*` prod).
 *  3. Cascade « Rupture Immédiate » via métafield variante `custom.out_of_stock`,
 *     exposé par l'endpoint Section Rendering `sections/oos-variant-data.liquid` :
 *     grise les saveurs (`is-oos-blocked`), badge « Épuisé », désactive le stepper ;
 *     pack infaisable → CTA désactivé « Revient vite ».
 *  4. Sync bouton ATC / indicateur stock / sticky quand l'app (ou la cascade)
 *     invalide le pack.
 *
 * Hooks refonte (remap depuis main-product-v2) :
 *  - ATC principal : `product-form-component [ref="addToCartButton"]`
 *    (texte dans `.add-to-cart-text__content`)
 *  - Sticky : `sticky-add-to-cart [ref="addToCartButton"]`
 *  - Stock : `.product-estimated-delivery` (`__dot--available/--oos`, `__text`)
 *  - Textes : data-attrs du script `#biscuits-bundle-options` (traduits en Liquid)
 *
 * Les sélecteurs `biscuits-*` (DOM rendu par l'app) sont inchangés.
 */

const optionsScript = document.getElementById('biscuits-bundle-options');

let bundleOptions = [];
if (optionsScript) {
	try {
		const json = JSON.parse(optionsScript.textContent);
		if (json?.options?.length) bundleOptions = json.options;
	} catch (e) {
		console.warn('[ax-biscuits-bundle] JSON #biscuits-bundle-options invalide', e);
	}
}

// Textes traduits (posés par Liquid sur le script d'options).
const texts = {
	rupture: optionsScript?.dataset.textRupture || 'Revient vite',
	soldOut: optionsScript?.dataset.textSoldout || 'Rupture de stock',
	unavailable: optionsScript?.dataset.textUnavailable || 'Non disponible',
	addToCart: optionsScript?.dataset.textAdd || 'Ajouter au panier',
};

// IDs de variantes (strings) flaggées « Rupture Immédiate » (custom.out_of_stock).
// Peuplé en async via la Section Rendering API (cf. enforceOutOfStockCascade).
let blockedVariants = new Set();

// True tant que le check out_of_stock est en cours (pilote l'état de chargement).
let oosChecking = false;

// ─── Hooks thème (refonte) ──────────────────────────────────

function getMainAddButton() {
	return document.querySelector('product-form-component [ref="addToCartButton"]');
}

function getStickyAddButton() {
	return document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
}

// ============================================================
// Auto-sélection de la 1re variante dispo quand la présélectionnée est OOS.
// Tourne AVANT l'init de l'app Biscuits (son script est defer).
// ============================================================
function patchOosPreselectedVariants() {
	for (const product of document.querySelectorAll('biscuits-bundle-product')) {
		const card = product.closest('.biscuits-bundle-item-card');
		if (!card) continue;

		const jsonScript = card.querySelector('.biscuits-bundle-item__json');
		if (!jsonScript) continue;

		let variants;
		try {
			variants = JSON.parse(jsonScript.textContent);
		} catch (e) {
			continue;
		}

		if (!variants || variants.length <= 1) continue;

		const select = product.querySelector('.biscuits-bundle-item__select');
		if (!select) continue;

		const preselectedId = product.getAttribute('data-biscuits-preselected');
		const requiredId = product.getAttribute('data-biscuits-required');
		const currentVariantId = preselectedId || requiredId || select.value;
		if (!currentVariantId) continue;

		const currentVariant = variants.find((v) => String(v.variant_id) === String(currentVariantId));
		if (!currentVariant || currentVariant.available) continue;

		const firstAvailable = variants.find((v) => {
			if (!v.available) return false;
			const option = select.querySelector(`option[value="${v.variant_id}"]`);
			return option && !option.disabled;
		});
		if (!firstAvailable) continue;

		const newVariantId = String(firstAvailable.variant_id);

		// 1. Valeur du <select>
		select.value = newVariantId;

		// 2. Attributs DOM de l'élément produit
		if (preselectedId) product.setAttribute('data-biscuits-preselected', newVariantId);
		if (requiredId) product.setAttribute('data-biscuits-required', newVariantId);
		product.setAttribute('data-biscuits-selected-variant-id', newVariantId);

		// 3. Patch du JSON de config du step (preselected_variants + required_variants)
		const stepJsonScript = product
			.closest('biscuits-bundle-step')
			?.querySelector('.biscuits-bundle-step__json');
		if (stepJsonScript) {
			try {
				const stepConfig = JSON.parse(stepJsonScript.textContent);
				if (stepConfig.rules) {
					for (const key of ['preselected_variants', 'required_variants']) {
						if (Array.isArray(stepConfig.rules[key])) {
							stepConfig.rules[key] = stepConfig.rules[key].map((id) =>
								String(id) === String(currentVariantId) ? newVariantId : String(id),
							);
						}
					}
					stepJsonScript.textContent = JSON.stringify(stepConfig);
				}
			} catch (e) {
				// Silencieux — l'app utilisera sa config d'origine.
			}
		}

		// 4. Patch du JSON de config du form
		const formJsonScript = product
			.closest('biscuits-bundle-form')
			?.querySelector('.biscuits-bundle-form__json');
		if (formJsonScript) {
			try {
				const formConfig = JSON.parse(formJsonScript.textContent);
				if (Array.isArray(formConfig.steps)) {
					for (const stepCfg of formConfig.steps) {
						for (const key of ['preselected_variants', 'required_variants']) {
							if (Array.isArray(stepCfg[key])) {
								stepCfg[key] = stepCfg[key].map((id) =>
									String(id) === String(currentVariantId) ? newVariantId : String(id),
								);
							}
						}
					}
					formJsonScript.textContent = JSON.stringify(formConfig);
				}
			} catch (e) {
				// Silencieux
			}
		}

		// 5. Change event pour les listeners déjà posés
		select.dispatchEvent(new Event('change', { bubbles: true }));
	}
}

patchOosPreselectedVariants();

// ============================================================
// Activation des items + dropdown custom coloré
// ============================================================
function activateBundleItemDirectly(bundleItem) {
	if (!bundleItem || bundleItem.classList.contains('active')) return;

	const titleElement = bundleItem.querySelector('.biscuits-bundle-item__title');
	if (
		(titleElement && titleElement.textContent.trim()) ||
		bundleItem.querySelector('.biscuits-bundle-item__select')
	) {
		bundleItem.classList.add('active');
	}
}

function processBundleItem(element) {
	if (element.dataset.biscuitsProcessed === 'true') return;

	const select = element.querySelector('.biscuits-bundle-item__select');
	const selectContainer = element.querySelector('.biscuits-bundle-item__options');
	const titleElement = element.querySelector('.biscuits-bundle-item__title');
	const detailsElement = element.querySelector('.biscuits-bundle-item__details');

	const bundleItem =
		select?.closest('.biscuits-bundle-item') ||
		element.querySelector('.biscuits-bundle-item') ||
		(element.classList.contains('biscuits-bundle-item') ? element : null);

	// Variant-title des biscuits-bundle-variant (lignes sans select) : look du
	// sélecteur custom — pastille carrée de la couleur du swatch + libellé —
	// et grammage remonté à côté du titre (« Matcha framboise / 200 g »)
	if (element.tagName === 'BISCUITS-BUNDLE-VARIANT' && bundleItem) {
		const variantTitle = bundleItem.querySelector('.biscuits-bundle-item__variant-title');
		if (variantTitle && !variantTitle.querySelector('.taste-icon')) {
			const [flavorName, grammage] = variantTitle.textContent
				.trim()
				.split(' / ')
				.map((part) => part.trim());
			const match = bundleOptions.find(
				(opt) => opt.label?.trim().toLowerCase() === flavorName.toLowerCase(),
			);

			const icon = document.createElement('span');
			icon.classList.add('taste-icon');
			icon.style.backgroundColor = match?.color || 'transparent';
			const label = document.createElement('span');
			label.textContent = flavorName;
			variantTitle.replaceChildren(icon, label);

			const lineTitle = bundleItem.querySelector('.biscuits-bundle-item__title');
			if (grammage && lineTitle && !lineTitle.querySelector('.biscuits-bundle-item__variant')) {
				const variantSpan = document.createElement('span');
				variantSpan.classList.add('biscuits-bundle-item__variant');
				variantSpan.textContent = grammage;
				lineTitle.appendChild(variantSpan);
			}

			// Sort la pill du titre → ligne 2 avec le stepper (titre+grammage / pill+stepper)
			const details = bundleItem.querySelector('.biscuits-bundle-item__details');
			if (details && variantTitle.parentElement !== details) {
				const quantity = details.querySelector('.biscuits-bundle-quantity');
				if (quantity) {
					details.insertBefore(variantTitle, quantity);
				} else {
					details.appendChild(variantTitle);
				}
			}
		}
	}

	if (!select || !titleElement) {
		if (bundleItem) activateBundleItemDirectly(bundleItem);
		return;
	}

	if (!bundleItem) return;

	if (select.options.length === 1) {
		const optionValue = select.options[0].value;
		const optionText = select.options[0].text;
		let variantSpan = titleElement.querySelector('.biscuits-bundle-item__variant');
		if (!variantSpan) {
			variantSpan = document.createElement('span');
			variantSpan.classList.add('biscuits-bundle-item__variant');
			titleElement.appendChild(variantSpan);
		}
		variantSpan.textContent = optionText;
		select.value = optionValue;
		selectContainer.style.display = 'none';
		bundleItem.classList.add('active');
		return;
	}

	if (select.options.length > 1) {
		selectContainer.style.display = 'none';
		select.classList.add('multiple-options');

		// Variante présélectionnée server-side (le select peut ne pas être à jour,
		// le script de l'app étant defer).
		const preselectedId =
			element.getAttribute('data-biscuits-preselected') ||
			element.getAttribute('data-biscuits-required') ||
			select.value;

		const allOptionTexts = Array.from(select.options).map((option) => option.text);
		const firstOptionParts = allOptionTexts[0].split(' / ');
		const redundantPart =
			firstOptionParts.find((part) =>
				allOptionTexts.every((optionText) => optionText.includes(part)),
			) || '';

		const customSelect = document.createElement('div');
		customSelect.classList.add('biscuits-bundle-item__custom-select');

		const selectedOption = document.createElement('div');
		selectedOption.classList.add('biscuits-bundle-item__custom-select-selected');

		// Nouvelle inté (Figma 5238-41960) : le fermé reste blanc/texte noir, la
		// couleur de la saveur vit dans une pastille carrée devant le label.
		const selectedIcon = document.createElement('span');
		selectedIcon.classList.add('taste-icon');
		const selectedLabel = document.createElement('span');
		selectedLabel.classList.add('biscuits-bundle-item__custom-select-label');
		const selectedGroup = document.createElement('span');
		selectedGroup.classList.add('biscuits-bundle-item__custom-select-current');
		selectedGroup.append(selectedIcon, selectedLabel);
		selectedOption.appendChild(selectedGroup);

		const optionsContainer = document.createElement('div');
		optionsContainer.classList.add('biscuits-bundle-item__custom-select-options');

		const updateSelectedOptionStyle = (cleanText) => {
			selectedLabel.textContent = cleanText;
			const match = bundleOptions.find(
				(opt) => opt.label?.trim().toLowerCase() === cleanText.trim().toLowerCase(),
			);
			selectedIcon.style.backgroundColor = match?.color || 'transparent';
		};

		for (const option of select.options) {
			const optionElement = document.createElement('div');
			optionElement.classList.add('option');
			if (option.disabled || blockedVariants.has(String(option.value))) {
				optionElement.classList.add('unavailable');
			}
			const cleanText = option.text.replace(` / ${redundantPart}`, '');

			const tasteIcon = document.createElement('span');
			tasteIcon.classList.add('taste-icon');

			const match = bundleOptions.find(
				(opt) => opt.label?.trim().toLowerCase() === cleanText.trim().toLowerCase(),
			);
			if (match) {
				if (match.color) tasteIcon.style.backgroundColor = match.color;
				if (match.txt_color) tasteIcon.style.color = match.txt_color;
			}

			optionElement.appendChild(tasteIcon);
			optionElement.appendChild(document.createTextNode(cleanText));
			optionElement.dataset.value = option.value;

			if (option.value === preselectedId) {
				optionElement.classList.add('selected');
				updateSelectedOptionStyle(cleanText);
			}

			optionElement.addEventListener('click', (e) => {
				e.stopPropagation();
				if (optionElement.classList.contains('unavailable')) return;

				select.value = optionElement.dataset.value;
				select.dispatchEvent(new Event('change', { bubbles: true }));

				for (const opt of optionsContainer.querySelectorAll('.option')) {
					opt.classList.remove('selected');
				}
				optionElement.classList.add('selected');
				updateSelectedOptionStyle(cleanText);

				selectedOption.classList.remove('open');
				optionsContainer.classList.remove('open');
			});

			optionsContainer.appendChild(optionElement);
		}

		selectedOption.addEventListener('click', () => {
			selectedOption.classList.toggle('open');
			optionsContainer.classList.toggle('open');
		});

		document.addEventListener('click', (e) => {
			if (!customSelect.contains(e.target)) {
				selectedOption.classList.remove('open');
				optionsContainer.classList.remove('open');
			}
		});

		let variantSpan = titleElement.querySelector('.biscuits-bundle-item__variant');
		if (!variantSpan) {
			variantSpan = document.createElement('span');
			variantSpan.classList.add('biscuits-bundle-item__variant');
			titleElement.appendChild(variantSpan);
		}
		variantSpan.textContent = redundantPart;

		// Sync du custom select quand le natif change (ex : présélection posée par l'app)
		select.addEventListener('change', () => {
			const matchingOption = optionsContainer.querySelector(
				`.option[data-value="${select.value}"]`,
			);
			if (!matchingOption) return;

			for (const opt of optionsContainer.querySelectorAll('.option')) {
				opt.classList.remove('selected');
			}
			matchingOption.classList.add('selected');

			const newText = matchingOption.textContent.trim();
			updateSelectedOptionStyle(newText);

			markCardIfAllVariantsBlocked(element);
		});

		customSelect.appendChild(selectedOption);
		customSelect.appendChild(optionsContainer);
		detailsElement.appendChild(customSelect);
		bundleItem.classList.add('active');
	}

	// État initial : si toutes les variantes sont déjà bloquées (rupture totale
	// d'un composant), on grise le sélecteur fermé. Re-appliqué après le check async.
	if (element.tagName === 'BISCUITS-BUNDLE-PRODUCT') {
		markCardIfAllVariantsBlocked(element);
	}

	element.dataset.biscuitsProcessed = 'true';
}

// ============================================================
// Grise la card produit quand TOUTES ses variantes sont bloquées (rupture
// immédiate metafield OU disabled app) — signal visuel « aucune saveur achetable ».
// ============================================================
function markCardIfAllVariantsBlocked(productEl) {
	if (!productEl || productEl.tagName !== 'BISCUITS-BUNDLE-PRODUCT') return;

	const card = productEl.closest('.biscuits-bundle-item-card') || productEl;
	const select = productEl.querySelector('.biscuits-bundle-item__select');
	if (!select) return;

	const options = Array.from(select.options);
	if (!options.length) return;

	const allBlocked = options.every(
		(opt) => blockedVariants.has(String(opt.value)) || opt.disabled,
	);

	card.classList.toggle('is-all-variants-blocked', allBlocked);
}

function deleteEmptyVariantText() {
	for (const element of document.querySelectorAll('.biscuits-bundle-item__variant')) {
		if (element.textContent.trim() === 'Default Title') element.classList.add('hidden');
	}
}

function processAllBundleElements() {
	for (const item of document.querySelectorAll('.biscuits-bundle-item:not(.active)')) {
		activateBundleItemDirectly(item);
	}

	for (const element of document.querySelectorAll('biscuits-bundle-product, biscuits-bundle-variant')) {
		processBundleItem(element);
	}

	deleteEmptyVariantText();

	// Overlay de chargement sur les steps fraîchement rendus pendant le check.
	applyCardsChecking();

	// Re-applique la cascade OOS si l'app a rebuild ses cards/options.
	if (blockedVariants.size) applyOosCascade();
}

processAllBundleElements();
setTimeout(processAllBundleElements, 500);

let processingTimeout = null;
const observer = new MutationObserver((mutations) => {
	const hasBundleChanges = mutations.some((mutation) =>
		Array.from(mutation.addedNodes).some(
			(node) =>
				node.nodeType === 1 &&
				(node.matches?.('biscuits-bundle-product, biscuits-bundle-variant, .biscuits-bundle-item') ||
					node.querySelector?.('.biscuits-bundle-item')),
		),
	);

	if (hasBundleChanges) {
		clearTimeout(processingTimeout);
		processingTimeout = setTimeout(processAllBundleElements, 200);
	}
});
observer.observe(document.body, { childList: true, subtree: true });
deleteEmptyVariantText();

// ============================================================
// Sync état OOS : bouton ATC, indicateur stock, sticky
// (déclenché quand l'app pose biscuits--disabled ou que la cascade metafield agit)
// ============================================================
function syncBundleOosState(originalStockText) {
	const addButton = getMainAddButton();
	if (!addButton) return;

	// Metafield OOS = un composant flaggé Rupture Immédiate.
	// App OOS = l'app a désactivé le bouton (composant réellement épuisé).
	const isMetafieldOos = addButton.dataset.oosMetafield === 'true';
	const isOos = isMetafieldOos || addButton.classList.contains('biscuits--disabled');

	// 1. État du bouton (miroir du CTA rupture des FP classiques : désactivé « Revient vite »)
	const textSpan = addButton.querySelector('.add-to-cart-text__content') || addButton.querySelector('span');
	if (isMetafieldOos) {
		addButton.classList.add('is-rupture-disabled');
		addButton.setAttribute('disabled', 'disabled');
		if (textSpan) textSpan.textContent = texts.rupture;
	} else if (isOos) {
		addButton.setAttribute('disabled', 'disabled');
		if (textSpan) textSpan.textContent = texts.unavailable;
	} else {
		addButton.classList.remove('is-rupture-disabled');
		addButton.removeAttribute('disabled');
		if (textSpan) textSpan.textContent = texts.addToCart;
	}

	// 2. Indicateur stock/livraison
	const stockIndicator = document.querySelector('.product-estimated-delivery');
	if (stockIndicator) {
		const dot = stockIndicator.querySelector('.product-estimated-delivery__dot');
		const text = stockIndicator.querySelector('.product-estimated-delivery__text');

		if (dot) {
			dot.classList.toggle('product-estimated-delivery__dot--available', !isOos);
			dot.classList.toggle('product-estimated-delivery__dot--oos', isOos);
		}
		if (text) {
			// Préserve la partie DLUO — seul le préfixe statut stock change.
			const currentText = text.textContent.trim();
			const dluoSeparator = currentText.indexOf('|');
			const dluoPart = dluoSeparator > -1 ? currentText.substring(dluoSeparator) : '';
			const stockText = isOos ? texts.soldOut : originalStockText;
			text.textContent = dluoPart ? `${stockText} ${dluoPart}` : stockText;
		}
	}

	// 3. Bouton sticky
	const stickyButton = getStickyAddButton();
	if (stickyButton) {
		if (isOos) stickyButton.setAttribute('disabled', 'disabled');
		else stickyButton.removeAttribute('disabled');
	}
}

function initBundleOosObserver() {
	const addButton = getMainAddButton();
	if (!addButton) return;

	const stockText = document.querySelector('.product-estimated-delivery__text');
	const originalStockText = stockText ? stockText.textContent.trim().split('|')[0].trim() : 'En stock';

	let oosTimeout = null;
	const oosObserver = new MutationObserver(() => {
		clearTimeout(oosTimeout);
		oosTimeout = setTimeout(() => syncBundleOosState(originalStockText), 100);
	});

	oosObserver.observe(addButton, {
		attributes: true,
		attributeFilter: ['class', 'disabled', 'data-oos-metafield'],
	});

	syncBundleOosState(originalStockText);
	// Re-check après l'init de l'app (script defer)
	setTimeout(() => syncBundleOosState(originalStockText), 1000);
}

initBundleOosObserver();

// ============================================================
// Cascade « Rupture Immédiate » (métafield variante custom.out_of_stock)
// Une variante flaggée doit être traitée indisponible même avec du stock réel.
// Si un step REQUIS devient infaisable, tout le pack passe OOS.
// IDs composants depuis le JSON server-rendered de l'app ; flags via la
// Section Rendering API (endpoint oos-variant-data, token-free).
// ============================================================

function collectBundleVariantHandles() {
	const map = new Map();
	for (const script of document.querySelectorAll('.biscuits-bundle-item__json')) {
		let data;
		try {
			data = JSON.parse(script.textContent);
		} catch (e) {
			continue;
		}
		for (const v of Array.isArray(data) ? data : [data]) {
			const handle = (v?.url || '').split('/products/')[1]?.split('?')[0];
			if (v?.variant_id && handle) map.set(String(v.variant_id), handle);
		}
	}
	return map;
}

async function fetchOosFlags(handles) {
	const result = {};
	await Promise.all(
		[...new Set(handles)].map(async (handle) => {
			try {
				const res = await fetch(`/products/${handle}?section_id=oos-variant-data`);
				if (!res.ok) return;
				const txt = await res.text();
				const start = txt.indexOf('{');
				const end = txt.lastIndexOf('}');
				if (start === -1 || end === -1) return;
				Object.assign(result, JSON.parse(txt.slice(start, end + 1)));
			} catch (e) {
				// Fail open : une erreur réseau ne doit pas bloquer un pack valide.
			}
		}),
	);
	return result;
}

function applyBlockedToOptions() {
	if (!blockedVariants.size) return;
	for (const opt of document.querySelectorAll('.biscuits-bundle-item__select option')) {
		if (blockedVariants.has(String(opt.value))) opt.disabled = true;
	}
	for (const opt of document.querySelectorAll('.biscuits-bundle-item__custom-select-options .option')) {
		if (blockedVariants.has(String(opt.dataset.value))) opt.classList.add('unavailable');
	}
	for (const select of document.querySelectorAll('.biscuits-bundle-item__select')) {
		if (!blockedVariants.has(String(select.value))) continue;
		const fallback = Array.from(select.options).find(
			(o) => !o.disabled && !blockedVariants.has(String(o.value)),
		);
		if (fallback) {
			select.value = fallback.value;
			select.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}
}

// Un step requis est cassé quand il ne peut plus atteindre son minimum avec des
// variantes ni indispo app ni bloquées metafield.
function isPackBrokenByOos() {
	let broken = false;
	for (const script of document.querySelectorAll('.biscuits-bundle-step__json')) {
		let cfg;
		try {
			cfg = JSON.parse(script.textContent);
		} catch (e) {
			continue;
		}
		const rules = cfg.rules || cfg;
		if (String(rules.required) !== '1') continue;
		const variants = (rules.variants || []).map(String);
		const unavailable = new Set((rules.variants_unavailable || []).map(String));
		const selectable = variants.filter((v) => !unavailable.has(v) && !blockedVariants.has(v));

		// Nombre de variantes DISTINCTES réellement nécessaires :
		// max(minimum_selectable, ceil(minimum_quantity / quantity_value)), borné
		// par maximum_selectable. Ex « 1 de chaque » (3 saveurs, min_qty 3, qv 1,
		// max_sel 3) → 3 requises → tout blocage casse le step.
		const minSelectable = rules.minimum_selectable || 1;
		const minQuantity = rules.minimum_quantity || 0;
		const quantityValue = rules.quantity_value || 1;
		const maxSelectable = rules.maximum_selectable || variants.length;
		let requiredCount = Math.max(
			minSelectable,
			minQuantity ? Math.ceil(minQuantity / quantityValue) : 0,
			1,
		);
		requiredCount = Math.min(requiredCount, maxSelectable);

		if (selectable.length < requiredCount) broken = true;
		for (const v of (rules.required_variants || []).map(String)) {
			if (blockedVariants.has(v)) broken = true;
		}
	}
	return broken;
}

// Cards bloquées : miroir du styling sold-out natif de l'app (`biscuits--unavailable`
// = gris + overlay « Épuisé ») pour que les ruptures metafield soient identiques
// aux variantes réellement épuisées.
function applyBlockedToCards() {
	if (!blockedVariants.size) return;
	for (const card of document.querySelectorAll('biscuits-bundle-variant')) {
		const id =
			card.getAttribute('data-biscuits-id') ||
			card.getAttribute('data-biscuits-selected-variant-id');
		if (!id || !blockedVariants.has(String(id))) continue;

		card.classList.add('biscuits--unavailable', 'is-oos-blocked');

		const checkbox = card.querySelector('.biscuits-bundle-item-checkbox');
		if (checkbox) {
			checkbox.setAttribute('aria-disabled', 'true');
			checkbox.checked = false;
		}

		const details = card.querySelector('.biscuits-bundle-item__details');
		if (details && !details.querySelector('.biscuits-bundle-badge--soldout')) {
			const badge = document.createElement('div');
			badge.className = 'biscuits-bundle-badge biscuits-bundle-badge--soldout';
			badge.textContent = texts.soldOut;
			const price = details.querySelector('.biscuits-bundle-price');
			if (price) details.insertBefore(badge, price);
			else details.appendChild(badge);
		}

		for (const el of card.querySelectorAll('.biscuits-bundle-quantity button, .biscuits-bundle-quantity input')) {
			el.setAttribute('disabled', 'disabled');
		}
	}
}

function applyOosCascade() {
	applyBlockedToOptions();
	applyBlockedToCards();
	for (const productEl of document.querySelectorAll('biscuits-bundle-product')) {
		markCardIfAllVariantsBlocked(productEl);
	}
	const addButton = getMainAddButton();
	if (!addButton) return;
	if (isPackBrokenByOos()) {
		// Déclenche l'oosObserver → syncBundleOosState (CTA grisé, sticky, stock).
		addButton.dataset.oosMetafield = 'true';
	}
}

// État de chargement CTA + indicateur stock pendant la résolution async des
// métafields (évite le flash « En stock → Rupture »).
function setBundleChecking(on) {
	oosChecking = on;
	for (const el of [getMainAddButton(), getStickyAddButton()]) {
		el?.classList.toggle('bundle-oos-checking', on);
	}
	for (const el of document.querySelectorAll('.product-estimated-delivery')) {
		el.classList.toggle('bundle-oos-checking-hide', on);
	}
	applyCardsChecking();
}

function applyCardsChecking() {
	for (const el of document.querySelectorAll('.biscuits-bundle-step__products')) {
		el.classList.toggle('bundle-oos-checking-cards', oosChecking);
	}
}

async function enforceOutOfStockCascade() {
	const variantHandles = collectBundleVariantHandles();
	if (!variantHandles.size) {
		setBundleChecking(false);
		return;
	}
	try {
		const flags = await fetchOosFlags([...variantHandles.values()]);
		blockedVariants = new Set(Object.keys(flags).filter((id) => flags[id] === true));
		if (blockedVariants.size) applyOosCascade();
	} finally {
		setBundleChecking(false);
	}
	if (blockedVariants.size) {
		// Re-applique après l'init du script defer de l'app.
		setTimeout(applyOosCascade, 600);
		setTimeout(applyOosCascade, 1500);
	}
}

function initOosCascade() {
	setBundleChecking(true);
	// Garde-fou : ne jamais laisser l'UI bloquée en loading si le fetch pend.
	setTimeout(() => setBundleChecking(false), 4000);
	enforceOutOfStockCascade();
}

initOosCascade();
